import dayjs from 'dayjs';
import { EAgeGroup } from 'data/types/generic.types';
import i18n from 'i18n';

export {
  dateConverter,
  dateFormatDB,
  dateFormatUI,
  dateShortenFormat,
  dateTimeFormatUI,
  dateTimeFormatDB,
  dateTimeWithoutSecUI,
  dateFormatWithWeekdayAndTime,
  dateFormatWithYearWeekdayAndTime,
  timepickerFormat,
} from '@idaho-aeyc/ui-kit';

export const getMaskEmail = (email: string) => {
  const [name, domain] = email.split('@');
  const maskedName = name.substring(0, 3) + '*'.repeat(name.length - 3);
  return `${maskedName}@${domain}`;
};

export const sortAgeGroups = (data: EAgeGroup[]) => {
  const compareAgeGroups = (a: EAgeGroup, b: EAgeGroup): number => {
    const order = Object.values(EAgeGroup);
    return order.indexOf(a) - order.indexOf(b);
  };

  const sortedData = data.sort(compareAgeGroups);

  return sortedData;
};

export const generateSpaceClassNames = (array: number[]) => {
  let classNames = ``;
  array.forEach((n, i) => {
    classNames += `
                .mx-${i} {
                    margin: 0 ${n}px !important;
                }
                .my-${i} {
                    margin: ${n}px 0 !important;
                }
                .m-${i} {
                    margin: ${n}px !important;
                }
                .mt-${i} {
                    margin-top: ${n}px !important;
                }
                .mb-${i} {
                    margin-bottom: ${n}px !important;
                }
                .ml-${i} {
                    margin-left: ${n}px !important;
                }
                .mr-${i} {
                    margin-right: ${n}px !important;
                }
                .pt-${i} {
                    padding-top: ${n}px !important;
                }
                .pb-${i} {
                    padding-bottom: ${n}px !important;
                }
                .pl-${i} {
                    padding-left: ${n}px !important;
                }
                .pr-${i} {
                    padding-right: ${n}px !important;
                }
                .px-${i} {
                    padding: 0 ${n}px !important;
                }
                .py-${i} {
                    padding: ${n}px 0 !important;
                }
                .p-${i} {
                    padding: ${n}px !important;
                }
            `;
  });

  return classNames;
};

export const checkDateIsBefore = (current: dayjs.Dayjs) => {
  const dateNow = dayjs(); // Assuming `dateNow` represents the current date
  return current && current.isBefore(dateNow);
};

export const checkDateIsAfter = (current: dayjs.Dayjs) => {
  const dateNow = dayjs(); // Assuming `dateNow` represents the current date
  return current && current.isAfter(dateNow);
};

export const convertStateToUpperCase = (value: string) => {
  return value.toUpperCase().replace(/\s+/g, '_');
};

/**
 * Phone number format options
 * Format: (123) 456-7890
 * Format: +11234567890
 */
export enum EPhoneNumberFormat {
  FORMATTED = 'formatted', // (123) 456-7890
  RAW = 'raw', // +11234567890
}

/**
 * Global configuration for phone number format
 * Change this value to apply consistent formatting across the entire app
 */
export const PHONE_NUMBER_FORMAT = EPhoneNumberFormat.FORMATTED;

/**
 * Format phone number based on configuration
 * @param value - Phone number string (e.g., "+11234567890" or "+111234567890")
 * @param format - Optional format override (defaults to PHONE_NUMBER_FORMAT)
 * @returns Formatted phone number or original value if not a valid US number
 */
export const formatPhoneNumber = (
  value: string | null,
  format: EPhoneNumberFormat = PHONE_NUMBER_FORMAT,
) => {
  if (!value) {
    return '';
  }

  // Remove all non-numeric characters except + at the start
  const cleaned = value.replace(/[^\d+]/g, '');

  let digits: string;

  // Handle different input formats
  if (cleaned.startsWith('+1')) {
    // Format: +11234567890
    digits = cleaned.substring(2);
  } else if (cleaned.startsWith('1') && cleaned.length === 11) {
    // Format: 11234567890 (exactly 11 digits with country code)
    digits = cleaned.substring(1);
  } else if (cleaned.length === 10) {
    // Format: 1234567890 (exactly 10 digits, no country code)
    digits = cleaned;
  } else {
    // Invalid format - return as-is
    return value;
  }

  // Validate we have exactly 10 digits after extraction
  if (digits.length !== 10) {
    // Invalid format - return as-is
    return value;
  }

  // Return based on format configuration
  if (format === EPhoneNumberFormat.RAW) {
    return `+1${digits}`;
  }

  // Format as (xxx) xxx-xxxx
  const formattedPhoneNumber = `(${digits.substring(0, 3)}) ${digits.substring(
    3,
    6,
  )}-${digits.substring(6, 10)}`;

  return formattedPhoneNumber;
};

export const getTextFromI18n = (
  keyInResource: string,
  language: 'en' | 'es' | undefined = 'en',
) => {
  return i18n.getResource(language, 'translation', keyInResource);
};

export const isWithin48Hours = (
  dateTime: string | Date | dayjs.Dayjs,
): boolean => {
  const now = dayjs();
  const givenDateTime = dayjs(dateTime);
  const diffInHours = Math.abs(givenDateTime.diff(now, 'hour'));

  return diffInHours <= 48;
};

export const combineDateAndTime = (
  dateValue?: string,
  startTime?: string,
): string => {
  if (!dateValue || !startTime) {
    return '';
  }

  // Parse the date string
  const date = dayjs(dateValue);

  // Parse the time in HH:mm:ss format
  const time = dayjs(startTime, 'HH:mm:ss');

  // Combine date with the new time and format it like the original
  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second())
    .millisecond(0)
    .format('MMM D, YYYY h:mm A'); // This will give you "Aug 30, 2025 10:00 AM"
};

export const formatStringToTitleCase = (str: string): string => {
  const wordToNumber: { [key: string]: string } = {
    One: '1',
    Two: '2',
    Three: '3',
  };

  return str
    .split('_')
    .map(word => {
      const capitalizedWord =
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      return wordToNumber[capitalizedWord] || capitalizedWord;
    })
    .join(' ');
};
